import { hashSeed, type Rng, seededRng } from './rng';
import type { Pair, RatedPlayer } from './types';

/**
 * Team balancing (R26, stats.md 4, gameplay.md 2.2). Pure, runs on the client so it works offline.
 *
 * Strength is OVR. An unrated player counts as the MEDIAN OVR of the rated players present (50
 * when nobody is rated), so a rookie neither wrecks nor rescues a split. A duo that played
 * together lately (`avoidPairs`) costs REPEAT_PENALTY per pair that lands on one team.
 *
 * Two search modes, same answer shape:
 *  - Exhaustive, when the number of candidate splits is at most EXHAUSTIVE_LIMIT (8 players into
 *    4 teams of 2 is 105 splits, 12 into 2 teams of 5 is 8316). It finds the true best.
 *  - Snake draft plus swap improvement, above that: a snake draft seeds the search, then swaps
 *    (between teams, and between a team and the bench) that lower the cost are taken until none
 *    helps, restarted from shuffled starts. Seeded, so the same input gives the same answer.
 */

export const REPEAT_PENALTY = 4;
export const DEFAULT_OVR = 50;
/** Above this many players the search is skipped (it would block the UI) and the answer is []. */
export const MAX_BALANCE_POOL = 24;
/** Candidate splits the exhaustive search will look at before the heuristic takes over. */
export const EXHAUSTIVE_LIMIT = 50_000;
const HEURISTIC_RESTARTS = 24;

export interface BalanceInput {
  players: readonly RatedPlayer[];
  /** Players per team (1..5). */
  teamSize: number;
  /** Teams to make, default 2. */
  teams?: number;
  avoidPairs?: readonly Pair[];
  /** How many options to return, best first. Default 3. */
  limit?: number;
  /**
   * Breaks ties between equal options and drives the heuristic restarts. Without it ties go by a
   * fixed order of ids and the heuristic seeds from the ids, so the answer is still repeatable.
   */
  rng?: Rng;
}

export interface TeamOption {
  /** Player ids per team. Team 0 holds the lowest-ranked id of the chosen players. */
  teams: string[][];
  /** Sum of OVR per team (unrated at the median). */
  strengths: number[];
  /** Largest strength minus smallest. */
  spread: number;
  /** Avoided duos that ended up together. */
  repeatPairs: number;
  /** spread + REPEAT_PENALTY * repeatPairs. Lower is better. */
  score: number;
  /** Rough win chance of team 0 against team 1 (two teams only, null otherwise). */
  winProbA: number | null;
  /** Players left out when more are present than seats. */
  sitting: string[];
}

export function medianOvr(players: readonly RatedPlayer[]): number {
  const rated = players
    .map((p) => p.ovr)
    .filter((v): v is number => v !== null)
    .sort((a, b) => a - b);
  if (rated.length === 0) return DEFAULT_OVR;
  const mid = rated.length >> 1;
  return rated.length % 2 ? (rated[mid] as number) : ((rated[mid - 1] as number) + (rated[mid] as number)) / 2;
}

/**
 * Rough chance that a side of strength `a` beats a side of strength `b`, both summed over `size`
 * players. A per-player gap of 12 OVR is about 73 percent. The scoreboard setup uses the same curve.
 */
export function winProbability(a: number, b: number, size: number): number {
  const gap = (a - b) / Math.max(1, size);
  return Math.round((1 / (1 + Math.exp(-gap / 12))) * 1000) / 1000;
}

function* combinations(n: number, k: number, start = 0, acc: number[] = []): Generator<number[]> {
  if (acc.length === k) {
    yield [...acc];
    return;
  }
  for (let i = start; i <= n - (k - acc.length); i++) {
    acc.push(i);
    yield* combinations(n, k, i + 1, acc);
    acc.pop();
  }
}

/** Split `items` into `teams` groups of `size`, each group listed once (no mirror images). */
function* partitions(items: readonly number[], teams: number, size: number): Generator<number[][]> {
  if (teams === 0) {
    yield [];
    return;
  }
  const [first, ...rest] = items;
  if (first === undefined) return;
  for (const others of combinations(rest.length, size - 1)) {
    const group = [first, ...others.map((i) => rest[i] as number)];
    const left = rest.filter((_, i) => !others.includes(i));
    for (const tail of partitions(left, teams - 1, size)) yield [group, ...tail];
  }
}

function choose(n: number, k: number): number {
  let r = 1;
  for (let i = 1; i <= k; i++) r = (r * (n - k + i)) / i;
  return Math.round(r);
}

function factorial(n: number): number {
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
}

/** How many distinct (sitters, teams) splits exist. Infinity is fine for big inputs. */
export function candidateCount(n: number, teams: number, size: number): number {
  const seats = teams * size;
  if (n < seats) return 0;
  return choose(n, seats) * (factorial(seats) / (factorial(size) ** teams * factorial(teams)));
}

const pairKey = (x: string, y: string) => (x < y ? `${x}|${y}` : `${y}|${x}`);

interface Ctx {
  players: readonly RatedPlayer[];
  strength: number[];
  avoid: Set<string>;
  teams: number;
  size: number;
}

function repeatIn(c: Ctx, group: readonly number[]): number {
  let repeat = 0;
  for (let a = 0; a < group.length; a++) {
    for (let b = a + 1; b < group.length; b++) {
      if (
        c.avoid.has(
          pairKey((c.players[group[a] as number] as RatedPlayer).id, (c.players[group[b] as number] as RatedPlayer).id),
        )
      ) {
        repeat += 1;
      }
    }
  }
  return repeat;
}

function optionOf(c: Ctx, groups: number[][], sitting: number[]): TeamOption {
  const idOf = (i: number) => (c.players[i] as RatedPlayer).id;
  // Team 0 is the team that holds the earliest player in input order; inside a team, input order.
  const ordered = groups.map((g) => [...g].sort((a, b) => a - b)).sort((a, b) => (a[0] as number) - (b[0] as number));
  const strengths = ordered.map((g) => g.reduce((s, i) => s + (c.strength[i] as number), 0));
  const repeat = ordered.reduce((s, g) => s + repeatIn(c, g), 0);
  const spread = Math.max(...strengths) - Math.min(...strengths);
  return {
    teams: ordered.map((g) => g.map(idOf)),
    strengths,
    spread,
    repeatPairs: repeat,
    score: spread + REPEAT_PENALTY * repeat,
    winProbA: c.teams === 2 ? winProbability(strengths[0] as number, strengths[1] as number, c.size) : null,
    sitting: [...sitting].sort((a, b) => a - b).map(idOf),
  };
}

const keyOf = (o: TeamOption) =>
  `${o.teams
    .map((t) => [...t].sort().join(','))
    .sort()
    .join('/')}~${[...o.sitting].sort().join(',')}`;

function exhaustive(c: Ctx, rng: Rng | undefined): TeamOption[] {
  const out: { o: TeamOption; tie: number }[] = [];
  const all = c.players.map((_, i) => i);
  for (const chosen of combinations(c.players.length, c.teams * c.size)) {
    const chosenSet = new Set(chosen);
    const sitting = all.filter((i) => !chosenSet.has(i));
    for (const split of partitions(chosen, c.teams, c.size)) {
      out.push({ o: optionOf(c, split, sitting), tie: rng ? rng() : 0 });
    }
  }
  return rank(out);
}

function rank(items: { o: TeamOption; tie: number }[]): TeamOption[] {
  const keyed = items.map((x) => ({ ...x, key: keyOf(x.o) }));
  keyed.sort(
    (a, b) =>
      a.o.score - b.o.score ||
      a.o.repeatPairs - b.o.repeatPairs ||
      a.tie - b.tie ||
      (a.key < b.key ? -1 : a.key > b.key ? 1 : 0),
  );
  return keyed.map((x) => x.o);
}

/** Snake order 0,1,..,k-1,k-1,..,0,0,1.. over teams. */
function snakeTeam(position: number, teams: number): number {
  const round = Math.floor(position / teams);
  const at = position % teams;
  return round % 2 === 0 ? at : teams - 1 - at;
}

function heuristic(c: Ctx, rng: Rng, limit: number): TeamOption[] {
  const n = c.players.length;
  const seats = c.teams * c.size;
  // assign[i]: team index, or -1 for the bench.
  const costOf = (assign: readonly number[]): number => {
    const sums = new Array<number>(c.teams).fill(0);
    const groups: number[][] = Array.from({ length: c.teams }, () => []);
    for (let i = 0; i < n; i++) {
      const t = assign[i] as number;
      if (t >= 0) {
        sums[t] = (sums[t] as number) + (c.strength[i] as number);
        (groups[t] as number[]).push(i);
      }
    }
    const spread = Math.max(...sums) - Math.min(...sums);
    return spread + REPEAT_PENALTY * groups.reduce((s, g) => s + repeatIn(c, g), 0);
  };
  const toOption = (assign: readonly number[]): TeamOption => {
    const groups: number[][] = Array.from({ length: c.teams }, () => []);
    const bench: number[] = [];
    for (let i = 0; i < n; i++) {
      const t = assign[i] as number;
      if (t >= 0) (groups[t] as number[]).push(i);
      else bench.push(i);
    }
    return optionOf(c, groups, bench);
  };

  const pool = new Map<string, TeamOption>();
  const remember = (assign: readonly number[]) => {
    const o = toOption(assign);
    pool.set(keyOf(o), o);
  };

  const climb = (start: number[]): number[] => {
    let cur = start;
    let curCost = costOf(cur);
    remember(cur);
    for (let guard = 0; guard < 400; guard++) {
      let bestCost = curCost;
      let best: number[] | null = null;
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          if (cur[i] === cur[j]) continue;
          const next = [...cur];
          next[i] = cur[j] as number;
          next[j] = cur[i] as number;
          const cost = costOf(next);
          if (cost < bestCost) {
            bestCost = cost;
            best = next;
          }
        }
      }
      if (!best) break;
      cur = best;
      curCost = bestCost;
      remember(cur);
    }
    return cur;
  };

  // Start 1: snake draft over the strongest `seats` players, the rest on the bench.
  const order = c.players
    .map((_, i) => i)
    .sort((a, b) => (c.strength[b] as number) - (c.strength[a] as number) || a - b);
  const snake = new Array<number>(n).fill(-1);
  order.slice(0, seats).forEach((idx, pos) => {
    snake[idx] = snakeTeam(pos, c.teams);
  });
  const optima: number[][] = [climb(snake)];

  // More starts: a random pick of who plays, dealt out in a random order.
  for (let r = 0; r < HEURISTIC_RESTARTS; r++) {
    const idx = c.players.map((_, i) => i);
    for (let i = idx.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [idx[i], idx[j]] = [idx[j] as number, idx[i] as number];
    }
    const start = new Array<number>(n).fill(-1);
    idx.slice(0, seats).forEach((pi, pos) => {
      start[pi] = pos % c.teams;
    });
    optima.push(climb(start));
  }

  // The neighbourhood of the best optimum fills the list when the caller wants several options.
  const best = optima.reduce((a, b) => (costOf(b) < costOf(a) ? b : a));
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      if (best[i] === best[j]) continue;
      const next = [...best];
      next[i] = best[j] as number;
      next[j] = best[i] as number;
      remember(next);
    }
  }
  return rank([...pool.values()].map((o) => ({ o, tie: rng() }))).slice(0, Math.max(limit, 1));
}

export function balanceTeams(input: BalanceInput): TeamOption[] {
  const teams = input.teams ?? 2;
  const size = input.teamSize;
  const seats = teams * size;
  const players = input.players;
  if (size < 1 || teams < 2 || players.length < seats || players.length > MAX_BALANCE_POOL) return [];
  const fill = medianOvr(players);
  const ctx: Ctx = {
    players,
    strength: players.map((p) => p.ovr ?? fill),
    avoid: new Set((input.avoidPairs ?? []).map(([a, b]) => pairKey(a, b))),
    teams,
    size,
  };
  const limit = input.limit ?? 3;
  if (candidateCount(players.length, teams, size) <= EXHAUSTIVE_LIMIT) {
    return exhaustive(ctx, input.rng).slice(0, limit);
  }
  const rng = input.rng ?? seededRng(hashSeed(players.map((p) => p.id).join(',')));
  return heuristic(ctx, rng, limit);
}

/**
 * Pick one of the best few options at random (the Shuffle button): `within` is how many of the
 * best options are in the draw.
 */
export function shuffleBest(options: readonly TeamOption[], rng: Rng, within = 5): TeamOption | null {
  const pool = options.slice(0, Math.max(1, within));
  return pool.length ? (pool[Math.floor(rng() * pool.length)] ?? null) : null;
}
